import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { PROJECTS, PROJECT_TYPES, PROJECT_SEGMENTS } from "../data/content";
import { Reveal, Overline } from "./Reveal";

const FilterRow = ({ label, options, active, onSelect, testPrefix }) => (
  <div className="flex flex-wrap items-center gap-2" data-testid={`${testPrefix}-filters`}>
    <span className="mr-1 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">{label}</span>
    {options.map((f) => (
      <button
        key={f.id}
        data-testid={`${testPrefix}-filter-${f.id}`}
        onClick={() => onSelect(f.id)}
        className={`border px-4 py-2 font-mono text-[11px] font-semibold uppercase tracking-[0.12em] transition-colors duration-200 ${
          active === f.id
            ? "border-navy bg-navy text-white"
            : "border-slate-300 bg-white text-slate-600 hover:border-navy hover:text-navy"
        }`}
      >
        {f.label}
      </button>
    ))}
  </div>
);

export const Projects = () => {
  const [type, setType] = useState("all");
  const [segment, setSegment] = useState("all");
  const visible = PROJECTS.filter(
    (p) =>
      (type === "all" || p.category === type) &&
      (segment === "all" || p.segment === segment)
  );

  return (
    <section id="projects" data-testid="projects-section" className="bg-white py-20 sm:py-28">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <Overline>Selected work</Overline>
          <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-5xl">
            Drawings that became buildings.
          </h2>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-10 space-y-3">
            <FilterRow label="Type" options={PROJECT_TYPES} active={type} onSelect={setType} testPrefix="project-type" />
            <FilterRow label="Segment" options={PROJECT_SEGMENTS} active={segment} onSelect={setSegment} testPrefix="project-segment" />
          </div>
        </Reveal>

        {visible.length === 0 ? (
          <div
            data-testid="projects-empty"
            className="mt-10 border border-dashed border-slate-300 bg-slate-50 p-14 text-center"
          >
            <p className="font-mono text-xs font-bold uppercase tracking-[0.2em] text-slate-400">
              Projects being added to this section — check back soon
            </p>
          </div>
        ) : (
          <motion.div layout className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-12 md:gap-8">
            <AnimatePresence mode="popLayout">
              {visible.map((p) => (
                <motion.article
                  layout
                  key={p.id}
                  data-testid={`project-card-${p.id}`}
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.96 }}
                  transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                  className={`group ${p.span}`}
                >
                  <div className="overflow-hidden border border-slate-200 bg-slate-50 transition-[box-shadow,border-color] duration-300 group-hover:border-navy group-hover:hard-shadow-sm">
                    <div className="relative overflow-hidden">
                      <img
                        src={p.image}
                        alt={p.title}
                        loading="lazy"
                        className="aspect-[4/3] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                      />
                      <span className="absolute left-3 top-3 bg-navy px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.15em] text-white">
                        {p.tag}
                      </span>
                      <span className="absolute right-3 top-3 bg-electric px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.15em] text-white">
                        {p.segment}
                      </span>
                    </div>
                    <div className="flex items-start justify-between gap-4 border-t border-slate-200 bg-white p-5">
                      <div>
                        <h3 className="font-display text-lg font-bold tracking-tight text-navy">{p.title}</h3>
                        <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.1em] text-slate-500">{p.meta}</p>
                      </div>
                      <span className="mt-1 h-2 w-2 shrink-0 rotate-45 bg-electric opacity-0 transition-opacity group-hover:opacity-100" />
                    </div>
                  </div>
                </motion.article>
              ))}
            </AnimatePresence>
          </motion.div>
        )}
      </div>
    </section>
  );
};
